import { Card } from "primereact/card";
import { SafeReportResponse } from "../Conversion";
import styles from "./LimitsCard.module.css";
import { TabPanel, TabView } from "primereact/tabview";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import * as client from "../../../client";
import { useContext } from "react";
import { ThemeContext } from "../../../themes/Theme";
import { Button } from "primereact/button";
import { testDeductibleOrLimitFunction } from "../../../api/routes";
import { useToast } from "../../../context-providers/Toast";
import { TooltipWrapper } from "../../../components/tooltip/TooltipWrapper";
import { readableNameFromPeril } from "../general-input/util";

interface LimitsCardProps {
  specialtyPropertyInfo: SafeReportResponse;
}

export const LimitsCard: React.FC<LimitsCardProps> = ({
  specialtyPropertyInfo,
}) => {
  const extractedLimits =
    specialtyPropertyInfo.report_json.limit_info?.extracted_limits ?? {};
  const manualLimits =
    specialtyPropertyInfo.report_json.limit_info?.manual_limits ?? {};
  const manualPerils = Object.keys(manualLimits);
  const { theme } = useContext(ThemeContext);
  const isLightMode = theme === "light";
  const toast = useToast();

  return (
    <Card title="Limits">
      <TabView>
        {Object.entries(extractedLimits).map(
          ([filename, extractedLimitsForFile]) => {
            return (
              <TabPanel header={filename} key={filename}>
                <div className={styles.verticalContainer}>
                  {Object.entries(extractedLimitsForFile).map(
                    ([peril, advancedLimit]) => {
                      const readablePerilName = readableNameFromPeril(
                        peril as client.peril_type
                      );
                      return (
                        <div
                          className={styles.verticalContainerSmall}
                          key={peril}
                        >
                          <div className={styles.horizontalContainer}>
                            <span className={styles.header}>
                              {readablePerilName}
                            </span>
                            <TooltipWrapper
                              tooltipText={advancedLimit.document_quotation}
                              tooltipProps={{ position: "left" }}
                            >
                              <i className="pi pi-info-circle" />
                            </TooltipWrapper>
                            <Button
                              label="Test"
                              outlined
                              size="small"
                              style={{ padding: "4px 6px" }}
                              onClick={async () => {
                                try {
                                  const result =
                                    await testDeductibleOrLimitFunction(
                                      specialtyPropertyInfo.report_id,
                                      filename,
                                      peril as client.peril_type,
                                      "limit"
                                    );
                                  toast.show({
                                    severity: "success",
                                    summary: "Limit Function Success",
                                    detail: `The ${readablePerilName} function ran successfully on ${result.num_addresses_run} addresses.`,
                                  });
                                } catch {
                                  toast.show({
                                    severity: "error",
                                    summary: "Limit Function Failure",
                                    detail: `Something went wrong when running the ${readablePerilName} function.`,
                                  });
                                }
                              }}
                            />
                          </div>
                          <span style={{ fontSize: "14px" }}>
                            {advancedLimit.short_limit_description}
                          </span>
                          <SyntaxHighlighter
                            language="python"
                            style={isLightMode ? oneLight : oneDark}
                            showLineNumbers
                            customStyle={{
                              fontSize: "13px",
                              lineHeight: "1.5",
                            }}
                          >
                            {advancedLimit.executable_python_function_str}
                          </SyntaxHighlighter>
                        </div>
                      );
                    }
                  )}
                </div>
              </TabPanel>
            );
          }
        )}
        {manualPerils.length > 0 && (
          <TabPanel header="Manual">
            <div className={styles.verticalContainer}>
              {Object.entries(manualLimits).map(([peril, advancedLimit]) => {
                const readablePerilName = readableNameFromPeril(
                  peril as client.peril_type
                );
                const hasFunction =
                  advancedLimit.executable_python_function_str !== "";
                return (
                  <div className={styles.verticalContainerSmall} key={peril}>
                    <div className={styles.horizontalContainer}>
                      <span className={styles.header}>{readablePerilName}</span>
                      {advancedLimit.document_quotation && (
                        <TooltipWrapper
                          tooltipText={advancedLimit.document_quotation}
                          tooltipProps={{ position: "left" }}
                        >
                          <i className="pi pi-info-circle" />
                        </TooltipWrapper>
                      )}
                      {hasFunction && (
                        <Button
                          label="Test"
                          outlined
                          size="small"
                          style={{ padding: "4px 6px" }}
                          onClick={async () => {
                            try {
                              const result =
                                await testDeductibleOrLimitFunction(
                                  specialtyPropertyInfo.report_id,
                                  null,
                                  peril as client.peril_type,
                                  "limit"
                                );
                              toast.show({
                                severity: "success",
                                summary: "Limit Function Success",
                                detail: `The ${readablePerilName} function ran successfully on ${result.num_addresses_run} addresses.`,
                              });
                            } catch {
                              toast.show({
                                severity: "error",
                                summary: "Limit Function Failure",
                                detail: `Something went wrong when running the ${readablePerilName} function.`,
                              });
                            }
                          }}
                        />
                      )}
                    </div>
                    <span style={{ fontSize: "14px" }}>
                      {advancedLimit.short_limit_description}
                    </span>
                    {hasFunction ? (
                      <SyntaxHighlighter
                        language="python"
                        style={isLightMode ? oneLight : oneDark}
                        showLineNumbers
                        customStyle={{
                          fontSize: "13px",
                          lineHeight: "1.5",
                        }}
                      >
                        {advancedLimit.executable_python_function_str}
                      </SyntaxHighlighter>
                    ) : (
                      <span
                        style={{
                          fontSize: "13px",
                          fontStyle: "italic",
                          color: "var(--text-color-secondary)",
                        }}
                      >
                        Function will be generated on next rerun.
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </TabPanel>
        )}
      </TabView>
    </Card>
  );
};
